import dayjs from 'dayjs';
import { Check, Copy } from 'lucide-react';
import { FC, memo, useState } from 'react';
import { Box, Button, Divider, useTheme } from '@mui/material';

import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import NewListRow from 'components/new-list-row';
import List, { HeadCell } from 'components/list/list';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { AppliedScenario } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-applied.type';
import { useIntl } from 'react-intl';
import ScenarioInfoGrid from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-info-grid';
import titleCase from 'utils/title-case';

interface ScenarioPlanListProps {
  scenario: AppliedScenario;
}

const planListColumns: HeadCell[] = [
  { id: 'fishGroup', label: 'Fish group' },
  { id: 'feedProgram', label: 'Feed program' },
  { id: 'harvestTarget', label: 'Harvest target' },
  { id: 'estHarvestDate', label: 'Est. harvest date' },
  { id: 'mortality', label: 'Mortality' },
];

const transferWeightsColumns: HeadCell[] = [
  { id: 'transferWeights', label: 'Transfer weights' },
  { id: 'feeds', label: 'Feeds', align: 'right' },
];

const ScenarioPlanList: FC<ScenarioPlanListProps> = ({ scenario }) => {
  const theme = useTheme();
  const intl = useIntl();
  const snackbarDispatch = useSnackbarDispatch();
  const [copiedFishGroupId, setCopiedCageId] = useState<string | null>(null);

  const copyToClipboard = async (fishGroupId: string) => {
    const cage = scenario.fishGroups.find((c) => c.fishGroupId === fishGroupId);
    if (!cage) return;

    const firstFeed = cage.feedDetails?.[0];
    const text = `Fish group: ${cage.fishGroupName}, Transfer weight: ${firstFeed?.transferWeight ?? 0} g, Feed program: ${firstFeed?.feedName || 'N/A'}`;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedCageId(fishGroupId);
      setTimeout(() => setCopiedCageId(null), 3000);
    } catch (err) {
      snackbarDispatch({
        type: SnackbarActionTypes.WARNING_SNACKBAR,
        payload: { msg: err instanceof Error ? err.message : String(err) },
      });
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      <ScenarioInfoGrid
        items={[
          { label: 'Applied as', value: titleCase(scenario.appliedAs) || 'N/A' },
          {
            label: 'Date applied',
            value: scenario.dateApplied ? dayjs(scenario.dateApplied).utc().format('DD-MM-YYYY') : 'N/A',
            noWrap: true,
          },
          ...(scenario.effectiveTo
            ? [
                {
                  label: 'Effective To',
                  value: dayjs(scenario.effectiveTo).utc().format('DD-MM-YYYY'),
                  noWrap: true,
                },
              ]
            : []),
        ]}
        dark
      />
      <List
        dark
        sx={{ ['.MuiTableCell-body']: { backgroundColor: `${theme.palette.lightBlue.o25}` } }}
        noResult={{
          show: !scenario.fishGroups?.length,
          text: intl.formatMessage({ id: 'general.noResults' }),
        }}
        options={planListColumns}>
        {scenario.fishGroups.map((cage) => (
          <NewListRow
            colSpan={8}
            key={cage.fishGroupId}
            body={
              <>
                <Divider sx={{ borderColor: theme.palette.darkBlue.o25 }} />
                <Box display="flex" flexDirection="column" justifyContent="space-between" gap={4} alignItems="center">
                  <List options={transferWeightsColumns}>
                    {cage.feedDetails.map((c) => (
                      <ListRow key={c.feedId}>
                        <ListCell>{c.transferWeight} g:</ListCell>
                        <ListCell align="right">{c.feedName}</ListCell>
                      </ListRow>
                    ))}
                  </List>
                  <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    startIcon={copiedFishGroupId === cage.fishGroupId ? <Check /> : <Copy />}
                    onClick={() => copyToClipboard(cage.fishGroupId)}
                    sx={{
                      mb: 3,
                      alignContent: 'center',
                      transition: 'all 0.3s ease-in-out',
                      ...(copiedFishGroupId === cage.fishGroupId && { color: theme.palette.success.main }),
                    }}>
                    {copiedFishGroupId === cage.fishGroupId ? 'Copied' : 'Copy weights and feeds'}
                  </Button>
                </Box>
              </>
            }>
            <ListCell>{cage.fishGroupName}</ListCell>
            <ListCell>{cage.feedPlan}</ListCell>
            <ListCell>{cage.harvestTarget}</ListCell>
            <ListCell>
              {cage.estimatedHarvestDate
                ? dayjs(cage.estimatedHarvestDate).utc().format('DD MMM YYYY')
                : 'No est. harvest date'}
            </ListCell>
            <ListCell>5%</ListCell>
          </NewListRow>
        ))}
      </List>
    </Box>
  );
};

export default memo(ScenarioPlanList);
